import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import {
  deleteQuestion,
  getQuizQuestions,
  getThematicQuizQuestions,
  setNewQuestionForm,
  setQuestionForm,
  setState,
  uploadQuizExcel,
  fetchThemes,
  fetchSubjects,
  fetchSubjectBasedClassesData,
  fetchQuizTypes,
  resetQuizFilters,
} from "./redux/schoolQuizSlice";
import API_BASE_URL, { SCHOOL_QUIZ_API_BASE_URL } from "../config";
import Popup from "../Popup";
import EditOrViewLawCollegeQuiz from "./EditOrViewSchoolQuiz";
import CommonTable from "./CommonTable";
import {
  fetchClassesThunk,
  fetchSubjectsByClassThunk,
} from "../../components/chapter/redux/chapterThunk";
import ThemeSelector from "../../School-Quiz-Mgt-admin/pages/ThemeSelector";

export default function GetSchoolQuizQuestions() {
  const dispatch = useDispatch();
  const {
    newQuestionForm,
    questionsList,
    openPopUp,
    mode,
    subjectsList,
    subjectBasedClasses,
    subjects,
    classes = [],
    themes = [],
    subThemes,
    quizTypesList,
    selectedSubThemes,
    subQuizType,
    loading,
    quizType,
  } = useSelector((state) => state.schoolQuiz || {}); // NOTE: sometimes classes live in state.chapter

  // If your classes/subjects live in chapter slice, also read them:
  const chapterClasses = useSelector((s) => s.chapter?.classes || []);
  const chapterSubjects = useSelector((s) => s.chapter?.subjects || []);

  // prefer chapter slice if available (common pattern in your codebase)
  const effectiveClasses = chapterClasses.length ? chapterClasses : classes;
  const effectiveSubjects = chapterSubjects.length ? chapterSubjects : subjects;

  const {
    questions = [],
    total_count,
    filtered_count,
    limit,
    offset,
  } = questionsList || {};

  const [error, setError] = useState("");
  const [groups, setGroups] = useState([]);

  // Tracks the most recent questions-fetch so an older, still in-flight
  // request can never overwrite a newer one's result.
  const activeQuestionsRequestRef = useRef(null);

  useEffect(() => {
    // fetch classes (chapter slice), themes, groups
    dispatch(fetchSubjects());
    dispatch(fetchThemes());
    dispatch(fetchQuizTypes());

    fetchGroups();
    return () => {
      dispatch(resetQuizFilters());
    };
    // dispatch(getQuizQuestions({ body: { limit: 100, offset: 0 } }));
  }, [dispatch]);

  useEffect(() => {
    if (newQuestionForm?.subject_id) {
      const subject_id = newQuestionForm.subject_id;

      // Clear previously loaded classes immediately
      dispatch(
        setState({
          key: "subjectBasedClasses",
          value: { data: [] },
        }),
      );

      dispatch(
        fetchSubjectBasedClassesData({
          subject_id,
        }),
      );
    } else {
      // No subject selected
      dispatch(
        setState({
          key: "subjectBasedClasses",
          value: { data: [] },
        }),
      );
    }
  }, [dispatch, newQuestionForm?.subject_id]);

  const fetchGroups = async () => {
    setError("");
    try {
      // console.debug("fetchGroups: calling", SCHOOL_QUIZ_API_BASE_URL);
      const res = await axios.get(`${SCHOOL_QUIZ_API_BASE_URL}/groups`);
      // console.debug("fetchGroups response:", res?.data);
      setGroups(Array.isArray(res.data) ? res.data : res.data?.data || []);
    } catch (err) {
      setError("Failed to fetch group data");
      console.error("fetchGroups error:", err);
    }
  };

  const userId = JSON.parse(localStorage.getItem("user") || "{}")?.id;

  // helper: fetch with current filters (keeps using getQuizQuestions)

  // inside component
  const fetchWithFilters = (overrides = {}, quizTypeParam = null) => {
    const qt = quizTypeParam ?? subQuizType;
    const body = {
      limit: overrides?.limit ?? limit ?? 100,
      offset: overrides?.offset ?? offset ?? 0,
      quiz_type: overrides?.quiz_type ?? qt,
      level: newQuestionForm?.difficulty_level || "",
    };

    // Add conditional properties (defaults pulled from current redux state)
    if (subQuizType === 2 || subQuizType === 4) {
      body.theme_id = selectedSubThemes?.length
        ? selectedSubThemes
        : newQuestionForm?.theme_id
          ? [newQuestionForm.theme_id]
          : [];
      body.group_id = newQuestionForm?.group_id || "";
    } else if (subQuizType === 1 || subQuizType === 3) {
      body.subject_id = newQuestionForm?.subject_id || "";
      body.class_id = newQuestionForm?.class_id || "";
    }

    // Apply explicit overrides LAST so a freshly-selected value (e.g. the
    // class_id just picked in the dropdown) always wins over the stale
    // value still sitting in the newQuestionForm redux state at the time
    // this function is called (dispatch() hasn't re-rendered yet).
    Object.assign(body, overrides);

    const cleanBody = { ...body };
    Object.keys(cleanBody).forEach((k) => {
      if (cleanBody[k] === "" || cleanBody[k] == null) delete cleanBody[k];
    });

    // console.debug("[fetchWithFilters] qt=", qt, "body:", cleanBody);

    // Cancel whatever questions-fetch is still in flight — only the request
    // we're about to send should be allowed to update the list.
    if (activeQuestionsRequestRef.current) {
      activeQuestionsRequestRef.current.abort();
    }

    let requestPromise;
    if (qt === 3 || qt === 4) {
      requestPromise = dispatch(getThematicQuizQuestions({ body: cleanBody }));
    } else if (qt === 1 || qt === 2) {
      requestPromise = dispatch(getQuizQuestions({ body: cleanBody }));
    } else {
      return;
    }

    activeQuestionsRequestRef.current = requestPromise;
    return requestPromise.unwrap().catch((err) => {
      // Swallow abort errors — they're expected when a newer request
      // supersedes this one, not a real failure.
      if (err?.name === "AbortError" || err === undefined) return;
      throw err;
    });
  };

  // on level change
  const handleLevelChange = (e) => {
    const level = e.target.value;
    dispatch(setNewQuestionForm({ key: "difficulty_level", value: level }));
    fetchWithFilters({ level, offset: 0 });
  };

  // useEffect(() => {
  //   if (newQuestionForm?.difficulty_level) {
  //     fetchWithFilters({ level: newQuestionForm?.difficulty_level, offset: 0 });
  //   }
  // }, [newQuestionForm?.difficulty_level]);

  // on quiz type change
  const handleQuizTypeChange = (e) => {
    const val = e.target.value;

    // update UI state
    dispatch(setState({ key: "quizType", value: val }));

    // clear filters in redux form
    dispatch(setNewQuestionForm({ key: "difficulty_level", value: "" }));
    dispatch(setNewQuestionForm({ key: "subject_id", value: "" }));
    dispatch(setNewQuestionForm({ key: "class_id", value: "" }));
    dispatch(setNewQuestionForm({ key: "theme_id", value: "" }));
    dispatch(setNewQuestionForm({ key: "group_id", value: "" }));
    dispatch(setState({ key: "selectedCategory", value: "" }));

    // IMPORTANT: call fetchWithFilters using the new value (avoid stale closure)
    fetchWithFilters({ offset: 0 }, val);
  };

  // When class changes, also dispatch subject fetch for that class using chapter thunk.
  // NOTE: do NOT call fetchWithFilters here when class_id is set — the
  // useEffect watching newQuestionForm.class_id already re-fetches once the
  // dispatch above lands in redux. Calling it here too fired a second,
  // racing request with a stale class_id (or none), which is what caused
  // the list to intermittently not update after selecting a class.
  const handleClassChange = (e) => {
    const class_id = e.target.value;
    console.debug("handleClassChange selected:", class_id);
    dispatch(setNewQuestionForm({ key: "class_id", value: class_id }));
    if (class_id) {
      // the chapter thunk expects a numeric id in your codebase
      const idNum = Number(class_id);
      dispatch(fetchSubjectsByClassThunk(idNum));
      // fetch is handled by the useEffect on newQuestionForm.class_id
    } else {
      // clear subjects if no class, and explicitly refetch the unfiltered
      // list since the effect won't fire a request when both class_id and
      // subject_id end up empty
      dispatch(setNewQuestionForm({ key: "subject_id", value: "" }));
      fetchWithFilters({ class_id: "", subject_id: "", offset: 0 });
    }
  };

  // Same reasoning: let the useEffect on newQuestionForm.subject_id own the
  // fetch so only one request goes out per change.
  const handleSubjectChange = (e) => {
    const subject_id = e.target.value;
    console.debug("handleSubjectChange selected:", subject_id);
    dispatch(setNewQuestionForm({ key: "subject_id", value: subject_id }));
    if (!subject_id) {
      fetchWithFilters({ subject_id: "", offset: 0 });
    }
  };

  const handleThemeChange = (e) => {
    const theme_id = e.target.value;
    dispatch(setNewQuestionForm({ key: "theme_id", value: theme_id }));
    fetchWithFilters({
      theme_id: [Number(theme_id)],
      offset: 0,
      group_id: newQuestionForm.group_id,
    });
    const filtered =
      themes.find((e) => e.theme_id === Number(theme_id))?.sub_theme || [];
    dispatch(
      setState({
        key: "subThemes",
        value: filtered,
      }),
    );
  };
  const handleGroupChange = (e) => {
    const group_id = e.target.value;
    dispatch(setNewQuestionForm({ key: "group_id", value: group_id }));
    fetchWithFilters({ group_id, offset: 0 });
  };

  // Map keys safely: support either `class_id`/`class_name` or `id`/`name`
  const renderClassOptions = () => {
    if (!effectiveClasses?.length)
      return <option value="">No classes found</option>;
    return effectiveClasses.map((c) => {
      const id = c.class_id ?? c.id ?? c.classId;
      const label = c.class_name ?? c.name ?? c.title ?? `Class ${id}`;
      return (
        <option key={id} value={id}>
          {label}
        </option>
      );
    });
  };

  const renderSubjectOptions = () => {
    if (!effectiveSubjects?.length)
      return <option value="">No subjects found</option>;
    return effectiveSubjects.map((s) => {
      const id = s.subject_id ?? s.id ?? s.subjectId;
      const label = s.subject_name ?? s.name ?? s.title ?? `Subject ${id}`;
      return (
        <option key={id} value={id}>
          {label}
        </option>
      );
    });
  };

  const renderThemeOptions = () => {
    if (!themes?.length) return <option value="">No themes found</option>;
    return themes.map((t) => {
      const id = t.theme_id ?? t.id;
      const label = t.name ?? t.title ?? `Theme ${id}`;
      return (
        <option key={id} value={id}>
          {label}
        </option>
      );
    });
  };

  // open view/edit popup and set question form (you had this earlier)
  const handleEditOrView = async (question, mode) => {
    console.log("question", question);
    dispatch(setState({ key: "openPopUp", value: true }));
    dispatch(setState({ key: "mode", value: mode }));
    dispatch(
      setQuestionForm({
        questionId: question.id,
        question: question.question,
        option_a: question.options?.A || "",
        option_b: question.options?.B || "",
        option_c: question.options?.C || "",
        option_d: question.options?.D || "",
        correct_option: question.correct_option || "",
        difficulty_level: question.level || question.difficulty_level || "",
        created_by: question.created_by_name || "",
        created_at: question.created_at || "",
        updated_by: question.updated_by_name || "",
        updated_at: question.updated_at || "",
        created_by_user_id: question.created_by || "",
        updated_at_user_id: question.updated_by || "",
        theme_id: question.theme_id || "",
        group_id: question.group_id || "",
        subject_id: question.subject_id || question.subject || "",
        class_id: question.class_id || question.class || "",
        themeData: question.themeDetails || [],
        // themeData: [{ theme_id: 4, sub_themes: [5, 16] }] || [],
      }),
    );
    const res = await dispatch(
      fetchSubjectBasedClassesData({ subject_id: Number(question.subject_id) }),
    ).unwrap();

    // Now res contains your actual data, not a promise
    dispatch(
      setQuestionForm({
        key: "subjectSpecificClasses",
        value: res,
      }),
    );
  };

  // delete with confirmation, dispatch, refresh list, and error handling
  const handleDeleteQuestion = async (id, updated_by) => {
    if (!window.confirm("Are you sure you want to delete this question?"))
      return;
    if (quizType === " ") {
      alert("Quiz type is not selected");
      return;
    }
    try {
      await dispatch(deleteQuestion({ id, updated_by, quizType })).unwrap();

      // success feedback
      alert("Question deleted successfully.");
      window.location.reload();

      // refresh the table - use your existing fetchWithFilters helper
      // If you fetch via CommonTable's fetchData it will pass pagination; here refresh first page
      fetchWithFilters({ offset: 0, limit: limit ?? 100 }, quizType);
    } catch (error) {
      // better error message
      const msg =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to delete question.";
      alert(msg);
      console.error("deleteQuestion error:", error);
    }
  };

  const renderGroupOptions = () => {
    if (!groups?.length) return <option value="">No groups found</option>;
    return groups.map((g) => {
      const id = g.group_id ?? g.id;
      const label = g.group_name ?? g.name ?? g.title ?? `Group ${id}`;
      return (
        <option key={id} value={id}>
          {label}
        </option>
      );
    });
  };

  // Columns + data (unchanged)
  const baseColumns = [
    { key: "id", label: "ID" },
    { key: "question", label: "Question" },
    { key: "option_A", label: "Option A" },
    { key: "option_B", label: "Option B" },
    { key: "option_C", label: "Option C" },
    { key: "option_D", label: "Option D" },
    { key: "correct_option", label: "Correct Option" },
    { key: "difficulty_level", label: "Difficulty Level" },
    // { key: "law_name", label: "Law Name" },
  ];

  let columns = [...baseColumns];
  if (quizType === "thematic") {
    columns.splice(
      columns.length - 1,
      0,
      // { key: "theme_name", label: "Theme Name" },
      // { key: "group_name", label: "Group Name" }
    );
  } else {
    columns.splice(
      columns.length - 1,
      0,
      // { key: "subject_name", label: "Subject" },
      // { key: "class_name", label: "Class" }
    );
  }
  columns.push({ key: "action", label: "Action (s)" });

  const data = (questions || []).map((q) => ({
    id: q.id,
    question: q.question || "-",
    option_A: q.options?.A || "-",
    option_B: q.options?.B || "-",
    option_C: q.options?.C || "-",
    option_D: q.options?.D || "-",
    correct_option: q.correct_option || "-",
    difficulty_level: q.level || q.difficulty_level || "-",
    law_name: q.law_name || "-",
    subject_name: q.subject_id || q.subject || "-",
    class_name: q.class_id || q.class || "-",
    theme_name: q.theme_name || q.theme || "-",
    group_name: q.group_name || q.group || "-",
    // inside your data mapping for each question (replace current action:)
    action: (
      <div style={{ display: "flex", gap: "8px" }}>
        <button
          onClick={() => {
            handleEditOrView(q, "view");

            // Wait for the API to finish and unwrap the actual response
          }}
        >
          View
        </button>

        {/* Edit */}
        <button onClick={() => handleEditOrView(q, "edit")}>Edit</button>

        {/* Delete */}
        <button
          onClick={
            () => handleDeleteQuestion(q.id, userId) // userId from localStorage or props
          }
        >
          Delete
        </button>
      </div>
    ),
  }));

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    // correct payload shape expected by reducer:
    dispatch(setNewQuestionForm({ key: name, value }));
    const numericFields = ["class_id", "subject_id", "group_id"];
    fetchWithFilters({
      theme_id: newQuestionForm?.theme_id
        ? [Number(newQuestionForm?.theme_id)]
        : selectedSubThemes
          ? selectedSubThemes
          : [],
      offset: 0,
      level: newQuestionForm?.difficulty_level || "",
      [name]: numericFields.includes(name) && value ? Number(value) : value,
    });
  };
  useEffect(() => {
    if (subQuizType) {
      fetchWithFilters({
        offset: 0,
        limit: 100,
        quiz_type: subQuizType,
      });
    }
  }, [subQuizType]);
  useEffect(() => {
    if (newQuestionForm?.class_id) {
      // class_id present -> this is the more specific filter, send it alone
      fetchWithFilters({
        offset: 0,
        limit: 100,
        quiz_type: subQuizType,
        subject_id: Number(newQuestionForm?.subject_id),
        class_id: Number(newQuestionForm?.class_id),
      });
    } else if (newQuestionForm?.subject_id) {
      fetchWithFilters({
        offset: 0,
        limit: 100,
        quiz_type: subQuizType,
        subject_id: Number(newQuestionForm?.subject_id),
      });
    }
  }, [newQuestionForm?.subject_id, newQuestionForm?.class_id]);
  return (
    <div>
      <h2 style={{ textAlign: "center" }}>School Quiz Questions</h2>
      {/* {selectedSubThemes || []} */}
      <div className="bg-white mb-8 max-w-4xl mx-auto shadow rounded-3 p-4">
        {/* Quiz Type */}

        <div className="row mb-3">
          <label className="col-sm-4 col-form-label font-medium text-gray-700">
            Quiz Type
          </label>
          <div className="col-sm-8 d-flex align-items-center gap-3">
            {[
              { label: "Live", value: 1 },
              { label: "Practice", value: 0 },
            ].map((option) => (
              <label
                key={option.value}
                className="form-check-label d-flex align-items-center gap-2"
              >
                <input
                  type="radio"
                  className="form-check-input"
                  name="quizType"
                  value={option.value}
                  checked={quizType === option.value} // loose equality to handle number/string
                  onChange={(e) => {
                    dispatch(
                      setState({
                        key: "quizType",
                        value: Number(e.target.value),
                      }),
                    );
                    dispatch(
                      setNewQuestionForm({ key: "theme_id", value: "" }),
                    );
                  }}
                  disabled={loading}
                />
                {option.label}
              </label>
            ))}
          </div>
          {quizType !== "" && (
            <>
              {(quizType === 1 || quizType === 0) && (
                <label className="col-sm-4 col-form-label font-medium text-gray-700">
                  Sub Quiz Type
                </label>
              )}
              <div className="col-sm-8 d-flex align-items-center gap-3 flex-wrap">
                {quizTypesList?.data
                  ?.filter((e) => e.is_live === quizType)
                  .map((quiz) => (
                    <label
                      key={quiz.id}
                      className="form-check-label d-flex align-items-center gap-2"
                    >
                      <input
                        type="radio"
                        className="form-check-input"
                        name="subQuizType"
                        value={quiz.id}
                        checked={subQuizType === quiz.id} // == handles string vs number
                        onChange={(e) => {
                          dispatch(
                            setState({
                              key: "subQuizType",
                              value: Number(e.target.value),
                            }),
                          );

                          dispatch(
                            setNewQuestionForm({
                              key: "subject_id",
                              value: "",
                            }),
                          );
                          dispatch(
                            setNewQuestionForm({ key: "class_id", value: "" }),
                          );
                          dispatch(
                            setNewQuestionForm({
                              key: "theme_id",
                              value: "",
                            }),
                          );
                        }}
                        disabled={loading}
                      />
                      {quiz.type.charAt(0).toUpperCase() + quiz.type.slice(1)}
                    </label>
                  ))}
              </div>
            </>
          )}
        </div>
        {(subQuizType === 1 || subQuizType === 2) && (
          <div>
            <div className="row mb-3">
              <label className="col-sm-4 col-form-label font-medium text-gray-700">
                Subject
              </label>
              <div className="col-sm-8">
                <select
                  className="form-select"
                  name="subject_id"
                  value={newQuestionForm?.subject_id || ""}
                  onChange={(e) => {
                    handleFieldChange(e);
                    fetchWithFilters({
                      quiz_type: subQuizType,
                      subject_id: Number(e.target.value),
                      offset: 0,
                      limit: 100,
                    });
                  }}
                  // disabled={loading || !newQuestionForm?.class_id}
                >
                  <option value="">-- Select --</option>
                  {(subjectsList?.data || [])
                    .filter((e) => e.status === 1)
                    .map((s) => (
                      <option key={s.subject_id} value={s.subject_id}>
                        {s.subject_name}
                      </option>
                    ))}
                </select>
              </div>
            </div>
            <div className="row mb-3">
              <label className="col-sm-4 col-form-label font-medium text-gray-700">
                Class
              </label>
              <div className="col-sm-8">
                <select
                  className="form-select"
                  name="class_id"
                  value={newQuestionForm?.class_id || ""}
                  onChange={(e) => {
                    handleFieldChange(e);
                    fetchWithFilters({
                      quiz_type: subQuizType,
                      subject_id: Number(newQuestionForm?.subject_id),
                      class_id: Number(e.target.value),
                      offset: 0,
                      limit: 100,
                    });
                  }}
                  disabled={loading || !newQuestionForm?.subject_id}
                >
                  <option value="">-- Select --</option>
                  {(subjectBasedClasses?.data || []).map((s) => (
                    <option key={s.subject_id} value={s.class_id}>
                      {s.class_name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}

        {(subQuizType === 3 || subQuizType === 4) && (
          <div>
            {/* Theme Dropdown */}
            <div className="row mb-3">
              <label className="col-sm-4 col-form-label text-gray-700">
                Theme
              </label>

              <div className="col-sm-8">
                <select
                  className="form-select"
                  name="theme_id"
                  value={newQuestionForm?.theme_id || ""}
                  onChange={handleThemeChange}
                  disabled={loading}
                >
                  <option value="">-- Select Theme --</option>
                  {themes.map((theme) => (
                    <option key={theme.theme_id} value={theme.theme_id}>
                      {theme.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            {newQuestionForm.theme_id && subThemes?.length > 0 && (
              <div>
                <div className="row mb-3">
                  <label className="col-sm-4 col-form-label text-gray-700">
                    Sub Themes
                  </label>
                  <div className="col-sm-8">
                    <select
                      className="form-select"
                      value={selectedSubThemes?.[0] || ""}
                      onChange={(e) => {
                        dispatch(
                          setState({
                            key: "selectedSubThemes",
                            value: [Number(e.target.value)],
                          }),
                        );
                        fetchWithFilters({
                          theme_id: [Number(e.target.value)],
                          group_id: Number(newQuestionForm.group_id),
                          level: newQuestionForm?.difficulty_level || "",
                          offset: 0,
                        });

                        // dispatch(
                        //   setState({
                        //     key: "selectedSubThemes",
                        //     value: updatedList,
                        //   })
                        // );
                      }}
                      style={{ minWidth: "250px", padding: "6px" }}
                    >
                      <option value="">Select Sub Theme</option>
                      {subThemes.map((e) => (
                        <option key={e.theme_id} value={e.theme_id}>
                          {e.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            )}

            <div className="row mb-3">
              <label className="col-sm-4 col-form-label font-medium text-gray-700">
                Groups
              </label>
              <div className="col-sm-8">
                <div className="col-sm-8">
                  <select
                    className="form-control"
                    name="group_id"
                    value={newQuestionForm?.group_id || ""}
                    onChange={handleFieldChange}
                    disabled={loading}
                  >
                    <option value="">-- Select Group --</option>
                    {groups?.map((g) => (
                      <option key={g.group_id} value={g.group_id}>
                        {g.group_name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Difficulty */}
        {subQuizType && (
          <div className="row mb-3">
            <label className="col-sm-4 col-form-label font-medium text-gray-700">
              Select the Question's Difficulty Level
            </label>
            <div className="col-sm-8">
              <select
                className="form-control"
                value={newQuestionForm?.difficulty_level || ""}
                onChange={(e) => {
                  // alert(subQuizType);
                  handleLevelChange(e);
                  const payload = {
                    level: e.target.value,
                    offset: 0,
                    quiz_type: subQuizType,
                    ...(subQuizType === 3 || subQuizType === 4
                      ? {
                          theme_id: newQuestionForm?.theme_id
                            ? [Number(newQuestionForm?.theme_id)]
                            : selectedSubThemes
                              ? selectedSubThemes
                              : [],
                          group_id: Number(newQuestionForm.group_id),
                        }
                      : subQuizType === 1 || subQuizType === 2
                        ? {
                            subject_id: Number(newQuestionForm?.subject_id),
                            class_id: Number(newQuestionForm?.class_id),
                          }
                        : []),
                  };

                  fetchWithFilters(payload);
                }}
              >
                <option value="">All</option>
                <option value="basic">Basic Level</option>
                <option value="advanced">Advanced Level</option>
                <option value="ultra">Ultra Level</option>
                <option value="docmaster">Docmaster</option>
              </select>
            </div>
          </div>
        )}

        {/* Questions Table */}
        {subQuizType && (
          <div style={{ height: "50vh", overflow: "auto" }}>
            <CommonTable
              total={total_count}
              filteredCount={filtered_count}
              columns={columns}
              data={data}
              initialRowsPerPage={100}
              searchableKeys={[
                "id",
                "question",
                "law_name",
                "option_A",
                "option_B",
                "option_C",
                "option_D",
              ]}
              fetchData={({ limit: l, offset: o }) =>
                fetchWithFilters({ limit: l, offset: o })
              }
            />
          </div>
        )}
        {openPopUp && (
          <Popup
            title={mode === "view" ? "View Question" : "Edit Question"}
            onClose={() => {
              dispatch(setState({ key: "openPopUp", value: false }));
              dispatch(setState({ key: "mode", value: "" }));
            }}
            maxWidth="700px"
            minHeight="600px"
            displayClose={true}
          >
            <EditOrViewLawCollegeQuiz quizType={subQuizType} />
          </Popup>
        )}
      </div>
    </div>
  );
}
